'use client';

import { FC } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';

/**
 * Props for `HeadlineFeaturesCta`.
 */
export type HeadlineFeaturesCtaProps =
  SliceComponentProps<Content.HeadlineFeaturesCtaSlice>;

/**
 * Component for highlighted text.
 */
const HighlightedText: FC<{ text: string }> = ({ text }) => {
  const words = text.split(' ');
  const conjunctions = [
    'and',
    'or',
    'but',
    'the',
    'a',
    'an',
    'in',
    'on',
    'at',
    'for',
    'to',
    'with',
  ];
  const qualifyingWords = words
    .map((word, i) => ({ word, index: i }))
    .filter(
      ({ word }) =>
        word.length > 3 && !conjunctions.includes(word.toLowerCase()),
    );

  const wordsToHighlight = qualifyingWords.slice(0, 3);

  return (
    <span>
      {words.map((word, i) => {
        const shouldHighlight = wordsToHighlight.some(
          ({ index }) => index === i,
        );
        return shouldHighlight ? (
          <span key={i} className="text-[#05f273] font-semibold">
            {word}{' '}
          </span>
        ) : (
          <span key={i}>{word} </span>
        );
      })}
    </span>
  );
};

/**
 * Component for "HeadlineFeaturesCta" Slices.
 */
const HeadlineFeaturesCta: FC<HeadlineFeaturesCtaProps> = ({ slice }) => {
  const title = asText(slice.primary.title || []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-10 md:py-[120px] text-white"
      style={{ backgroundColor: '#0F423A' }}
    >
      <div className="max-w-[1200px] mx-auto px-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[100px] items-center">
          <div className="space-y-6">
            <h2 className="text-5xl font-semibold leading-[56px] mb-6">
              {title ? (
                <HighlightedText text={title} />
              ) : (
                <PrismicRichText field={slice.primary.title} />
              )}
            </h2>
            <div className="text-lg leading-7 text-[#c4c4c4] mb-10">
              <PrismicRichText field={slice.primary.description} />
            </div>
            <PrismicNextLink
              field={slice.primary.primary_cta}
              className="secondary-button py-2 px-6 text-sm inline-flex items-center group w-fit"
            >
              <ArrowRightIcon className="arrow-icon w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 group-hover:mr-2 transition-all duration-300" />
              <span>
                {slice.primary.button_label || 'Check Our Success Stories'}
              </span>
            </PrismicNextLink>
          </div>

          <div className="flex flex-col gap-6">
            {slice.primary.features.map((item, index) => {
              // Progressive sizing: first card smallest, getting bigger, left-aligned
              const getCardSize = (index: number) => {
                switch (index) {
                  case 0:
                    return 'w-[80%]'; // Smallest, left-aligned
                  case 1:
                    return 'w-[90%]'; // Medium, left-aligned
                  case 2:
                    return 'w-[95%]'; // Almost full width
                  case 3:
                    return 'w-[100%]'; // Largest, full width
                  default:
                    return 'w-[100%]';
                }
              };

              // Get plain text from rich text field
              const descriptionText = asText(item.feature_description || []);

              return (
                <div
                  key={index}
                  className={`p-6 text-lg leading-7 ${getCardSize(index)}`}
                  style={{
                    borderRadius: '16px',
                    background:
                      'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)',
                    boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {descriptionText ? (
                    <HighlightedText text={descriptionText} />
                  ) : (
                    <PrismicRichText field={item.feature_description} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeadlineFeaturesCta;
